import { useState, useEffect } from 'react';
import { useLocalization } from '../Provider/Provider.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { setInputValueByRef } from '../../utils/input.js';
import { format, parseFromCustomString } from '../../utils/date.js';

const useDatepicker = ({
  defaultValue: _,
  calendar,
  onBlur,
  onChange,
  value,
  ...props
}, ref) => {
  const inputRef = useProxiedRef(ref);
  const {
    formatting
  } = useLocalization();
  const [internalValue, setInternalValue] = useState(value ? format(value, formatting.date) : ''); // update internal value if it changed 'externally'

  useEffect(() => {
    if (value) {
      const formattedValue = format(value, formatting.date);

      if (formattedValue !== internalValue) {
        setInternalValue(formattedValue);
      }
    } else {
      setInternalValue('');
    }
  }, [value]); // event handlers

  const handleInputBlur = event => {
    event.persist();
    const valueAsDate = parseFromCustomString(event.target.value);
    const formattedValue = valueAsDate ? format(valueAsDate) || '' : '';
    event.target.value = formattedValue;

    if (onChange) {
      event.detail = valueAsDate;
      onChange(event);
    } else {
      // update the internal value to use the formatted date
      setInternalValue(formattedValue);
    }

    if (onBlur) {
      onBlur(event);
    }
  };

  const handleInputChange = event => {
    setInternalValue(event.target.value);
  };

  const handleChange = date => {
    setInputValueByRef(inputRef.current, format(date, formatting.date), 'focusout');
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.target.dispatchEvent(new Event('focusout', {
        bubbles: true
      }));
    }
  };

  const inputProps = { ...props,
    autoComplete: 'off',
    onBlur: handleInputBlur,
    onChange: handleInputChange,
    onKeyDown: handleKeyDown,
    ref: inputRef,
    type: 'text',
    value: internalValue
  };
  const calendarProps = { ...calendar,
    onChange: handleChange,
    value
  };
  return {
    input: inputProps,
    calendar: calendarProps
  };
};

export { useDatepicker };
//# sourceMappingURL=useDatepicker.js.map
